import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy, CheckCircle2, Clock } from "lucide-react";

const CATEGORIES = ["System / App issue", "Login / Access", "Printer / Hardware", "Network / Internet", "Data / Records", "General"];

export const ITSupportDialog = ({ trigger }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ category: "System / App issue", subject: "", description: "" });

  const submit = useMutation({
    mutationFn: () => api.post("/it-requests", form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["it-requests"] });
      setDone(true);
    },
  });

  const reset = () => { setForm({ category: "System / App issue", subject: "", description: "" }); setDone(false); };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTimeout(reset, 200); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="contact-it-btn" className="bg-navy hover:bg-navy-900 text-white rounded-lg">
            <LifeBuoy className="mr-2 h-4 w-4" /> Contact IT Team
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="it-support-dialog">
        {done ? (
          <div className="flex flex-col items-center py-6 text-center" data-testid="it-success">
            <div className="rounded-full bg-emerald-50 p-4"><CheckCircle2 className="h-10 w-10 text-emerald-600" /></div>
            <h3 className="mt-4 font-heading text-lg font-bold text-navy">Request submitted</h3>
            <p className="mt-2 max-w-sm text-sm text-slate-600">
              Our IT team will look into this matter and resolve it at the earliest, within <b>8 hours</b>.
            </p>
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
              <Clock className="h-3.5 w-3.5" /> Target resolution: 8 hours
            </div>
            <Button className="mt-6 rounded-lg bg-navy text-white hover:bg-navy-900" data-testid="it-success-close" onClick={() => setOpen(false)}>
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-navy">Raise a Request</DialogTitle>
              <DialogDescription>Report any IT or system issue. Our team resolves requests within 8 hours.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="it-category" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject</Label>
                <Input
                  data-testid="it-subject"
                  className="mt-1"
                  placeholder="e.g. Cannot open the Attendance page"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                />
              </div>
              <div>
                <Label>Describe the issue</Label>
                <Textarea
                  data-testid="it-description"
                  className="mt-1"
                  rows={4}
                  placeholder="What happened? What were you trying to do?"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="it-submit"
                disabled={!form.subject.trim() || submit.isPending}
                onClick={() => submit.mutate()}
                className="bg-navy hover:bg-navy-900 text-white rounded-lg"
              >
                {submit.isPending ? "Submitting…" : "Submit Request"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
